"use client";
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useState } from "react";
import { useTranslations } from "next-intl";

interface FaqParams {
  domain?: "Home" | "Citadel"; // 可选参数，默认为"Home"
  itemCount?: number; // 可选参数，默认为8
}

export function MemberShipFAQ() {
  const t = useTranslations("Home");
  return (
    <Box mb={8}>
      <Typography variant="h4" fontSize="var(--font-size-xl)" fontWeight="600" mb={4.5}>
        {t("faq.title")}
      </Typography>
      <FAQList domain="Home" itemCount={14} />
    </Box>
  );
}

export function HomeFAQSection({ domain="Home", itemCount=14 }: FaqParams) {
  const t = useTranslations("Home");
  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" }, // 小屏幕上改为纵向布局
        justifyContent: "space-between",
        alignItems: { xs: "center", md: "flex-start" }, // 小屏幕上居中对齐
        gap: { xs: 5, md: 4 }, // 小屏幕上增加间距
      }}>
      <Box
        sx={{
          width: { xs: "100%", md: "24rem" }, // 小屏幕上宽度100%
          textAlign: { xs: "center", md: "left" }, // 小屏幕上文本居中
          mb: { xs: 2, md: 0 }, // 小屏幕上增加底部外边距
          px: { xs: 2, md: 0 }, // 小屏幕上增加水平内边距
        }}>
        <Typography variant="h4" sx={{ mb: 3, fontSize: "var(--font-size-3xl)", fontWeight: "600" }}>
          {t("faq.title")}
        </Typography>
      </Box>
      <Box
        sx={{
          width: { xs: "100%", md: "43.125rem" }, // 小屏幕上宽度100%
          px: { xs: 2, md: 0 }, // 小屏幕上增加水平内边距
        }}>
        <FAQList domain={domain} itemCount={itemCount} />
      </Box>
    </Container>
  );
}

export function FAQList({ domain, itemCount }: FaqParams) {
  const t = useTranslations(domain);
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // 根据传入的itemCount动态生成FAQ列表
  const faqs = Array.from({ length: itemCount ?? 8 }, (_, index) => {
    const itemNum = index + 1;
    return {
      question: t(`faq.item${itemNum}.question` as any),
      answer: t(`faq.item${itemNum}.answer` as any),
    };
  });

  return (
    <Box>
      {faqs.map((faq, index) => (
        <Accordion
          key={index}
          expanded={expanded === `panel${index}`}
          onChange={handleChange(`panel${index}`)}
          sx={{
            mb: 3,
            bgcolor: "background.paperMedium",
            borderRadius: "0.75rem",
            boxShadow: "none",
            "&:before": { display: "none" },
            "&:first-of-type": {
              borderTopLeftRadius: "0.75rem",
              borderTopRightRadius: "0.75rem",
            },
            "&:last-of-type": {
              borderBottomLeftRadius: "0.75rem",
              borderBottomRightRadius: "0.75rem",
            },
          }}>
          <AccordionSummary
            expandIcon={
              expanded === `panel${index}` ? (
                <RemoveIcon sx={{ fontSize: "1.5rem" }} />
              ) : (
                <AddIcon sx={{ fontSize: "1.5rem" }} />
              )
            }
            sx={{
              py: 2.5,
              px: 3,
              transition: "all 0.2s ease-in-out",
              "&.Mui-expanded": {
                py: 2.5,
              },
              "& .MuiAccordionSummary-content": {
                margin: 0,
                transition: "margin 0.2s ease-in-out",
                "&.Mui-expanded": {
                  my: 0,
                },
              },
              "& .MuiAccordionSummary-expandIconWrapper": {
                color: "text.secondary",
              },
            }}>
            <Typography
              variant="body1"
              fontWeight="600"
              sx={{ fontSize: "var(--font-size-s)", color: "text.primary2" }}>
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              borderTop: 1,
              borderColor: "divider",
              mx: 2,
              px: 1,
              py: 4,
            }}>
            <Typography variant="body2" sx={{ fontSize: "var(--font-size-s)", color: "text.primary2" }}>
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
